import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, DecimalPipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  ClrDatagridModule,
  ClrDatagridSortOrder,
  ClrDatagridFilterInterface,
  ClrDatagridStringFilterInterface,
  ClrIconModule,
  ClrLoadingModule,
  ClrModalModule,
} from '@clr/angular';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Subject } from 'rxjs';
import {fileIcon, filterGridCircleIcon, ClarityIcons, twoWayArrowsIcon} from '@cds/core/icon';

import { Warehouse } from '../../../_shared/interfaces';
import { AlertService, IAlertType, WarehouseStoreService } from '../../../_shared/services';
import { filesBaseUrl } from '../../../_shared/utils/rest-url';
import { getTenantStorage } from '../../../_shared/utils/tenant-context';

export interface MovementTypeOption {
  value: Warehouse['movement_type'];
  label: string;
}

export class MovementTypeFilter implements ClrDatagridFilterInterface<Warehouse> {
  changes = new Subject<void>();
  selected = new Set<string>();

  readonly options: MovementTypeOption[] = [
    { value: 'stock_init',        label: 'Lager initialisiert' },
    { value: 'incoming',          label: 'Wareneingang' },
    { value: 'outgoing',          label: 'Warenausgang' },
    { value: 'internal_transfer', label: 'Interne Umbuchung' },
    { value: 'return_inbound',    label: 'Rückgabe Eingang' },
    { value: 'return_outbound',   label: 'Rückgabe Ausgang' },
    { value: 'scrap',             label: 'Ausschuss' },
    { value: 'provision_in',      label: 'Beistellung Eingang' },
    { value: 'provision_out',     label: 'Beistellung Ausgang' },
    { value: 'inventory_in',      label: 'Inventur (Zugang)' },
    { value: 'inventory_out',     label: 'Inventur (Abgang)' },
  ];

  isActive(): boolean { return this.selected.size > 0; }

  accepts(item: Warehouse): boolean {
    return !this.isActive() || this.selected.has(item.movement_type);
  }

  toggle(value: string): void {
    this.selected.has(value) ? this.selected.delete(value) : this.selected.add(value);
    this.changes.next();
  }
}

class WarehouseStringFilter implements ClrDatagridStringFilterInterface<Warehouse> {
  /**
   * Der Wert, mit dem die Seite ueber ?drawing= / ?serial= / ?heat= aufgerufen
   * wurde -- oder null, wenn ohne Vorbelegung.
   *
   * Solange im Filterfeld noch genau dieser Wert steht, wird **exakt**
   * verglichen. Wer aus den Bauteil-Details auf "Bewegungen ansehen" klickt,
   * meint dieses eine Bauteil; mit dem Teilstring-Vergleich erschienen auch
   * alle Nummern, die damit anfangen -- "0360-10106 Ind. B" zog
   * "0360-10106 Ind. B Material" mit herein, und das ist ein anderes Bauteil.
   *
   * Sobald jemand im Feld etwas aendert, gilt wieder der Teilstring: dann ist
   * es eine Suche, und "0360-10106" soll die ganze Familie zeigen.
   */
  exactFor: string | null = null;

  constructor(private field: keyof Warehouse) {}

  accepts(item: Warehouse, search: string): boolean {
    const val = String(item[this.field] ?? '');
    if (this.exactFor !== null && search === this.exactFor) {
      return val === this.exactFor;
    }
    return val.toLowerCase().includes(search.toLowerCase());
  }
}

@Component({
  selector: 'app-warehouse-overview',
  standalone: true,
  imports: [
    NgIf, NgForOf, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault,
    DatePipe, DecimalPipe,
    RouterLink,
    ClrDatagridModule,
    ClrIconModule,
    ClrLoadingModule,
    ClrModalModule,
    NgxExtendedPdfViewerModule,
  ],
  templateUrl: './warehouse-overview.component.html',
  styleUrl: './warehouse-overview.component.sass',
})
export class WarehouseOverviewComponent implements OnInit, OnChanges {
  /** Subnav-Status: 'movements' (alle ohne stock_init) | 'incoming' | 'outgoing' | 'stock_init' */
  @Input() status: string = 'movements';

  entries: Warehouse[] = [];
  visibleEntries: Warehouse[] = [];
  loading = true;
  descSort = ClrDatagridSortOrder.DESC;

  private static readonly STATUS_GROUPS: Record<string, ReadonlySet<string>> = {
    incoming:   new Set(['incoming', 'return_inbound', 'provision_in']),
    outgoing:   new Set(['outgoing', 'return_outbound', 'scrap', 'provision_out']),
    stock_init: new Set(['stock_init']),
    provision:  new Set(['provision_in', 'provision_out']),
  };

  /** Laufender Bestand je Zeichnungsnummer nach dieser Bewegung (key = warehouse id) */
  private readonly balances = new Map<number, number>();

  showPdf = false;
  modalUrl = '';
  modalContent = '';

  readonly movementFilter = new MovementTypeFilter();
  readonly drawingFilter  = new WarehouseStringFilter('technical_drawing');
  readonly serialFilter   = new WarehouseStringFilter('serial_nr');
  readonly heatFilter     = new WarehouseStringFilter('heat_nr');
  /** Vorbelegung der Filter via ?drawing=… / ?serial=… / ?heat=… (Component-Details, Header-Suche) */
  drawingFilterValue = '';
  serialFilterValue  = '';
  heatFilterValue    = '';

  private filesDirectory = filesBaseUrl();

  constructor(
    private waress: WarehouseStoreService,
    private alertDispatcher: AlertService,
    private route: ActivatedRoute,
  ) {
    ClarityIcons.addIcons(fileIcon, filterGridCircleIcon, twoWayArrowsIcon);
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.drawingFilterValue = params.get('drawing') ?? '';
      this.serialFilterValue  = params.get('serial') ?? '';
      this.heatFilterValue    = params.get('heat') ?? '';
      // Vorbelegt heisst: genau dieses Bauteil, diese Seriennummer, diese
      // Charge. Siehe WarehouseStringFilter.exactFor.
      this.drawingFilter.exactFor = this.drawingFilterValue || null;
      this.serialFilter.exactFor  = this.serialFilterValue  || null;
      this.heatFilter.exactFor    = this.heatFilterValue    || null;
    });
    this.waress.getAllWarehouses().subscribe({
      next: data => {
        this.entries = data?.object ?? [];
        this.computeBalances();
        this.applyStatus();
        if (this.entries.length === 0) {
          this.alertDispatcher.show(
            { message: 'Keine Lagerbewegungen gefunden.', clrAlertType: IAlertType.WARNING },
            2000
          );
        }
      },
      complete: () => { this.loading = false; },
      error:    () => { this.loading = false; },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyStatus();
  }

  private applyStatus(): void {
    const group = WarehouseOverviewComponent.STATUS_GROUPS[this.status];
    this.visibleEntries = group
      ? this.entries.filter(e => group.has(e.movement_type))
      : this.entries.filter(e => e.movement_type !== 'stock_init');
  }

  /**
   * Bestand je Zeichnungsnummer kumuliert über alle Bewegungen (inkl. stock_init)
   * in chronologischer Reihenfolge — unabhängig von Sortierung und Subnav-Filter.
   */
  private computeBalances(): void {
    this.balances.clear();
    const chronological = [...this.entries].sort((a, b) =>
      a.date === b.date ? a.id - b.id : (a.date < b.date ? -1 : 1)
    );
    const perDrawing = new Map<string, number>();
    for (const e of chronological) {
      if (!e.technical_drawing) continue;
      const balance = (perDrawing.get(e.technical_drawing) ?? 0) + this.sign(e.movement_type) * e.number_of_pieces;
      perDrawing.set(e.technical_drawing, balance);
      this.balances.set(e.id, balance);
    }
  }

  balanceFor(entry: Warehouse): number | null {
    return this.balances.get(entry.id) ?? null;
  }

  /** Determines which "Vorgang" link type to show */
  vorgangType(entry: Warehouse): 'order' | 'delivery' | 'transport' | 'component' | 'job' | 'none' {
    if (entry.delivery_id > 0)  return 'delivery';
    if (entry.transport_id > 0) return 'transport';
    if (entry.order_id > 0)     return 'order';
    if (entry.is_provision && entry.job_id > 0) return 'job';
    if (entry.movement_type === 'stock_init' && entry.technical_drawing) return 'component';
    return 'none';
  }

  openPdf(fileId: string, description: string): void {
    const token = getTenantStorage('access_token');
    this.modalUrl = `${this.filesDirectory}/download_file?file_id=${fileId}&token=${token}`;
    this.modalContent = description || 'Dokument';
    this.showPdf = true;
  }

  movementLabel(type: string): string {
    return this.movementFilter.options.find(o => o.value === type)?.label ?? type;
  }

  movementClass(type: string): string {
    switch (type) {
      case 'stock_init':        return 'label label-purple';
      case 'incoming':          return 'label label-success';
      case 'outgoing':          return 'label label-danger';
      case 'internal_transfer': return 'label label-info';
      case 'return_inbound':    return 'label label-warning';
      case 'return_outbound':   return 'label label-warning';
      case 'scrap':             return 'label';
      case 'provision_in':      return 'label label-blue';
      case 'provision_out':     return 'label label-blue';
      case 'inventory_in':      return 'label label-purple';
      case 'inventory_out':     return 'label label-purple';
      default:                  return 'label';
    }
  }

  private sign(type: string): 1 | -1 {
    return (type === 'outgoing' || type === 'return_outbound' || type === 'scrap'
      || type === 'provision_out' || type === 'inventory_out') ? -1 : 1;
  }

  pieceSign(type: string): string {
    return this.sign(type) < 0 ? '−' : '+';
  }

  pieceClass(type: string): string {
    return this.sign(type) < 0 ? 'out' : 'in';
  }

}
